import { useState } from "react";
import { getApiUrl, getBaseUrl } from "@/config/appConfig";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, ArrowRight, Shield, RefreshCw } from "lucide-react";
import axios from "axios";

// Using centralized config
const getAPI = () => getApiUrl();

export default function LoginPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA States
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [resending, setResending] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${getAPI()}/auth/login`,
        formData,
        { withCredentials: true }
      );
      
      if (response.data.requires_2fa) {
        // 2FA required - show verification step
        setRequires2FA(true);
        toast.success("Verification code sent to your email");
      } else if (response.data.success) {
        // Store token in localStorage for cross-origin auth
        if (response.data.token) {
          localStorage.setItem('session_token', response.data.token);
        }
        toast.success("Login successful!");
        window.location.href = '/dashboard';
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Login failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (twoFACode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setVerifying2FA(true);
    try {
      const response = await axios.post(
        `${getAPI()}/auth/2fa/verify`,
        {
          email: formData.email,
          code: twoFACode,
          remember_device: rememberDevice
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Store token in localStorage for cross-origin auth
        if (response.data.token) {
          localStorage.setItem('session_token', response.data.token);
        }
        toast.success("Login successful!");
        window.location.href = '/dashboard';
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Verification failed";
      toast.error(detail);
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await axios.post(
        `${getAPI()}/auth/2fa/send-code`,
        null,
        { 
          params: { email: formData.email },
          withCredentials: true 
        }
      );
      toast.success("New code sent to your email");
    } catch (error) {
      toast.error("Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to backend Google OAuth endpoint using centralized config
    const backendUrl = getBaseUrl();
    if (!backendUrl) {
      toast.error('Configuration error. Please try again later.');
      return;
    }
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-violet-600 to-indigo-700 p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">SP</span>
            </div>
            <span className="text-2xl font-bold text-white">SeatPilot</span>
          </Link>
        </div>
        
        <div className="text-white">
          <h1 className="text-4xl font-bold mb-4">Welcome back!</h1>
          <p className="text-violet-200 text-lg mb-8">
            Log in to manage your seating layouts, track bookings, and grow your event business.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-violet-100">Create unlimited seating layouts</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-violet-100">Real-time seat booking management</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-violet-100">Powerful API for seamless integration</span>
            </div>
          </div>
        </div>
        
        <p className="text-violet-300 text-sm">
          Powered by TicketKart &copy; {new Date().getFullYear()}
        </p>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">SP</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">SeatPilot</span>
            </Link>
          </div>
          
          <Card className="p-8 shadow-xl border-0">
            {!requires2FA ? (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to your account</h2>
                  <p className="text-gray-500">Enter your credentials to access your dashboard</p>
                </div>
                
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="you@company.com"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Enter your password"
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-violet-600 hover:text-violet-700"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Sign in"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleLogin}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </>
            ) : (
              <>
                {/* 2FA Verification Step */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-violet-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Identity</h2>
                  <p className="text-gray-500">
                    We sent a 6-digit code to <span className="font-medium">{formData.email}</span>
                  </p>
                </div>
                
                <form onSubmit={handleVerify2FA} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Enter verification code</label>
                    <Input
                      type="text"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                      required
                    />
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={rememberDevice}
                        onChange={(e) => setRememberDevice(e.target.checked)}
                        className="rounded border-gray-300 text-violet-600 focus:ring-violet-500" 
                      />
                      <span className="text-sm text-gray-600">Remember this device for 30 days</span>
                    </label>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
                    disabled={verifying2FA || twoFACode.length !== 6}
                  >
                    {verifying2FA ? "Verifying..." : "Verify & Sign In"}
                  </Button>
                </form>
                
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-500 mb-2">Didn&apos;t receive the code?</p>
                  <button
                    onClick={handleResendCode}
                    disabled={resending}
                    className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1 mx-auto"
                  >
                    <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
                    {resending ? "Sending..." : "Resend code"}
                  </button>
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => {
                      setRequires2FA(false);
                      setTwoFACode("");
                      setRememberDevice(false);
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700 w-full text-center"
                  >
                    ← Back to login
                  </button>
                </div>
              </>
            )}
            
            {!requires2FA && (
              <p className="text-center text-sm text-gray-600 mt-6">
                Don&apos;t have an account?{" "}
                <Link to="/signup" className="text-violet-600 hover:text-violet-700 font-medium">
                  Sign up for free
                </Link>
              </p>
            )}
          </Card>
          
          <p className="text-center text-xs text-gray-500 mt-6">
            By signing in, you agree to our{" "}
            <Link to="/terms" className="text-violet-600 hover:underline">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" className="text-violet-600 hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
